import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  Renderer2,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { ActivatedRoute } from '@angular/router';
import {
  BaseSiteService,
  CmsService,
  GlobalMessageService,
  GlobalMessageType,
  ProductService,
  TranslationService,
} from '@spartacus/core';
import { BREAKPOINT, BreakpointService } from '@spartacus/storefront';
import moment from 'moment';
import { DeviceDetectorService } from 'ngx-device-detector';
import { EMPTY, Observable, Subscription } from 'rxjs';
import { auditTime, filter, map, switchMap, take, tap } from 'rxjs/operators';
import { browserRefresh } from 'src/app/app.component';
import { CheckoutConstants } from 'src/app/model/checkout.model';
import {
  BACKORDER_SORT_OPTIONS,
  BackorderSortLabelsModel,
  INVOICE_SORT_OPTIONS,
  InvoiceSortLabelsModel,
  OrderHistoryStatus,
  OrderView,
  SORT_MAPPING,
  SORT_OPTIONS,
  SortLabelsModel,
} from 'src/app/model/order-history.model';
import { BaseStoreService } from 'src/app/service/base-store/base-store.service';
import { CheckoutServiceExtends } from 'src/app/service/checkout/checkout.service';
import { ComponentService } from 'src/app/service/component.service';
import { ShimanoCountryService } from 'src/app/service/country/shimano-country.service';
import { ShimanoLanguageService } from 'src/app/service/language/shimano-language.service';
import { MyaccountPersonalInfoService } from 'src/app/service/myaccount/myaccount-personal-info.service';
import {
  GetOrdersPayload,
  OrderHistoryPaginationModel,
  OrderHistoryService,
} from 'src/app/service/orders/order-history.service';
import { CustomProductService } from 'src/app/service/product/custom-product.service';
import { StorageService } from 'src/app/service/storage/storage.service';
import { CurrentBusinessType } from 'src/app/utility/current-businesstype';
import { ShimanoUtilMethod } from 'src/app/utility/methods.util';
import { OrderHistoryFilterComponent } from './order-history-filter/order-history-filter.component';

export function getOrderHistoryStatusIcon(statusCode: string): string {
  let statusIcon: string;
  switch (statusCode) {
    case OrderHistoryStatus.OPEN:
      statusIcon = 'open';
      break;
    case OrderHistoryStatus.CLOSED:
      statusIcon = 'closed';
      break;
    case OrderHistoryStatus.BACKORDER:
      statusIcon = 'back-order';
      break;
  }
  return statusIcon;
}

@Component({
  selector: 'app-order-history',
  templateUrl: './order-history.component.html',
  styleUrls: ['./order-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class OrderHistoryComponent implements OnInit, OnDestroy, AfterContentChecked, AfterViewInit {
  @ViewChild(OrderHistoryFilterComponent, { static: false }) orderHistoryFilter: any;
  @ViewChild('sidenav', { static: false }) sidenav: MatSidenav;
  @ViewChild('matContainer', { static: false }) matContainer: MatSidenavContainer;
  public browserRefresh: boolean;

  header = document.querySelector('header');
  bottomHeaderSlot = document.querySelector('.BottomHeaderSlot');
  footer = document.querySelector('footer');
  orderHistoryElement = document.querySelector('cx-page-layout.OrderHistoryPageTemplate');
  mode: FormControl;
  selectedFilter = 0;
  totalNumberOfResults = 0;
  hasData: number;
  hasOpenSideNav = false;
  bodyContent: any = document.querySelector('.BodyContent');
  orderHistoryContentHeight: any;
  styleMode: any;
  orderHistory: any;
  orderHistoryResults: any[] = [];
  isGetOrdersBackOrdersDone = true;
  showNoResult = false;
  resultsSpinner = true;
  filterSearch: any = null;
  filterSearchBy: any = null;
  dateFormatted = '';
  skipVariantCategory: string;
  default = 'default';
  sideNavOpened = false;
  currentLanguage: string;
  features: any = {};
  orderView: string;
  isDesktop: boolean;
  queryParams = new URLSearchParams(window.location.search);
  businessType: string;
  message: string;
  showSrp = false;

  // Observables
  baseSite$: Observable<string> = this.baseService.getActive();
  activeCountry$: Observable<string> = this.countryService.getActive();
  activeLanguage$: Observable<string> = this.languageService.getActive();

  // Observables: Features Config
  showDeliveryFee$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(
        activeCountry,
        CheckoutConstants.DELIVERY_FEE_IN_ORDER_HISTORY
      )
    )
  );

  showGstInclusive$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.GST_INCLUSIVE)
    )
  );

  showExpectedShipmentDate$: Observable<boolean> = this.activeCountry$.pipe(
    take(1),
    switchMap((activeCountry) =>
      this.checkoutServiceExtends.isCheckoutFeatureEnabled$(activeCountry, CheckoutConstants.EXPECTED_SHIPMENT_DATE)
    )
  );

  orderHistoryPayload: GetOrdersPayload = {
    currentPage: 0,
    dir: 'desc',
    fields: 'DEFAULT',
    filterSearchData: '',
    filterSearchBy: '',
    fromDate: '',
    orderStatus: [],
    orderTypes: [],
    pageSize: 12,
    sort: 'orderDate',
    toDate: '',
  };

  PaginationModel: OrderHistoryPaginationModel = {
    currentPage: 0,
    pageSize: 0,
    sort: null,
    totalPages: 0,
    totalResults: 0,
    pageNumber: 0,
  };

  // Sort By Values: Order / Backorder View
  sort = 'orderdatedesc';
  sortOptions = SORT_OPTIONS;
  bsortOptions = BACKORDER_SORT_OPTIONS;
  sortLabels: Observable<SortLabelsModel> = this.orderHistoryService.getSortLabels();
  bsortLabels: Observable<BackorderSortLabelsModel>;

  // Sort By Values: Invoice View
  invoiceSort = INVOICE_SORT_OPTIONS[0].code;
  invoiceSortOptions = INVOICE_SORT_OPTIONS;
  invoiceSortLabels: Observable<InvoiceSortLabelsModel> = this.orderHistoryService.getInvoiceSortLabels();

  // Subscriptions
  srpSubscription$: Subscription;
  clearFilterSubscription$: Subscription;
  filterSearchSubscription$: Subscription;
  filterSearchBySubscription$: Subscription;
  setOrderStatusSubscription$: Subscription;
  setOrderTypeSubscription$: Subscription;
  getOrderHistorySubscription$: Subscription;
  removeOrderStatusSubscription$: Subscription;
  selectedFilterSubscription$: Subscription;
  totalOrdersSubscription$: Subscription;
  fromDateSubscription$: Subscription;
  toDateSubscription$: Subscription;
  triggerOrdersSubscription$: Subscription;
  breakpointSubscription$: Subscription;
  reloadLanguageForOrderTypeSubscription$: Subscription;

  // Getters
  get baseSiteId(): string {
    let baseSiteId: string;
    this.baseSite$.pipe(take(1)).subscribe((baseSiteData) => (baseSiteId = baseSiteData));
    return baseSiteId;
  }

  get sessionStore(): string {
    return this.storageService.getSessionStorageItem('orderState');
  }

  get isMobile$(): Observable<boolean> {
    return this.breakpointService.breakpoint$.pipe(map((breakpoint) => breakpoint === BREAKPOINT.xs));
  }

  constructor(
    protected cms: CmsService,
    protected orderHistoryService: OrderHistoryService,
    protected componentService: ComponentService,
    protected cdr: ChangeDetectorRef,
    protected productService: ProductService,
    public baseStoreService: BaseStoreService,
    protected baseService: BaseSiteService,
    protected customProductService: CustomProductService,
    protected translationService: TranslationService,
    protected countryService: ShimanoCountryService,
    protected checkoutServiceExtends: CheckoutServiceExtends,
    protected breakpointService: BreakpointService,
    protected languageService: ShimanoLanguageService,
    protected currentBusinesstype: CurrentBusinessType,
    public deviceDetectorService: DeviceDetectorService,
    protected router: ActivatedRoute,
    protected storageService: StorageService,
    protected messageService: GlobalMessageService,
    protected myAccountPersonalInforService: MyaccountPersonalInfoService,
    protected renderer: Renderer2,
    protected elementRef: ElementRef
  ) {
    this.storageService.setSessionStorageItem('orderState', 'orders');
    this.baseStoreService
      .getBasestoreData()
      .pipe(take(1))
      .subscribe((baseStoreData) => {
        this.dateFormatted = baseStoreData.dateFormat.replace(/\//g, '-');
      });

    this.customProductService
      .getSkipVariant()
      .pipe(take(1))
      .subscribe((resp) => {
        this.skipVariantCategory = resp[this.baseSiteId]
          ? resp[this.baseSiteId].defaultVariantCategory
          : resp[this.default].defaultVariantCategory;
      });

    // get customized order history features
    this.orderHistoryService
      .getFeatures()
      .pipe(take(1))
      .subscribe((features: any) => {
        this.features = { ...features, ...this.features };
        this.applyWideDiv();
        if (Object.keys(this.features).length) {
          if (this.router.snapshot.queryParamMap.get('item')) {
            this.orderHistoryPayload.filterSearchData = this.router.snapshot.queryParamMap.get('item');
            this.orderHistoryPayload.filterSearchBy = 'itemNumber';
            this.orderHistoryService.getOrdersBackOrders(this.orderHistoryPayload, 'orders');
          }
          if (this.router.snapshot.queryParamMap.get('view') === OrderView.BACKORDER) {
            this.storageService.setSessionStorageItem('orderState', OrderView.BACKORDER);
            this.orderView = OrderView.BACKORDER;
            this.orderHistoryService.getOrderHistory$.next([OrderView.BACKORDER, false, true]);
          } else if (this.router.snapshot.queryParamMap.get('view') === OrderView.INVOICE) {
            this.storageService.setSessionStorageItem('orderState', OrderView.INVOICE);
            this.orderView = OrderView.INVOICE;
            this.orderHistoryService.getOrderHistory$.next([OrderView.INVOICE, false, true]);
          } else {
            this.getOrderHistory(false, true);
          }
        }
        this.cdr.detectChanges();
      });
  }

  ngOnInit() {
    this.mode = new FormControl('over');

    this.triggerOrdersSubscription$ = this.orderHistoryService.triggerOrders$.subscribe(
      ([status, isInOrderHistoryPage]) => {
        if (status && isInOrderHistoryPage) {
          this.resultsSpinner = true;
          setTimeout(() => {
            this.getOrderHistory();
          }, 2000);
        }
      }
    );

    //set orderState value
    this.browserRefresh = browserRefresh;
    if (this.browserRefresh) {
      this.storageService.setSessionStorageItem('orderState', 'orders');
    }

    this.getInitSrpToggleValue();
    this.srpSubscription$ = this.componentService.srpToggle$.subscribe((checked) => {
      this.showSrp = checked;
      this.cdr.detectChanges();
    });

    this.getOrderHistorySubscription$ = this.orderHistoryService.getOrderHistory$
      .pipe(
        filter(([orderView, onScroll, onInit]) => orderView !== null && Object.keys(this.features).length > 0),
        auditTime(1000),
        tap(([orderView, onScroll, onInit]) => {
          const isFirstEmit = !this.orderView;
          this.orderView = orderView ?? OrderView.ORDERS;
          if (!onScroll) {
            this.resultsSpinner = true;
            this.orderHistoryPayload.currentPage = 0;
            this.orderHistoryResults = [];
            this.showNoResult = true;
          }
          if (onInit) {
            this.setDefaultFilter(isFirstEmit);
            this.applyWideDiv();
          }
          this.cdr.detectChanges();
        }),
        switchMap(([orderView, onScroll]) => {
          if (orderView === OrderView.INVOICE) {
            return EMPTY;
          } else {
            return this.orderHistoryService.getOrdersBackOrders(this.orderHistoryPayload, orderView);
          }
        })
      )
      .subscribe(
        (items) => {
          this.setOrderBackordersPage(items);
        },
        (err) => {
          this.setError();
        }
      );

    this.filterSearchBySubscription$ = this.orderHistoryService.filterSearchBy$.subscribe((filterSearchBy) => {
      if (this.orderHistoryPayload.filterSearchBy !== filterSearchBy) {
        this.filterSearchBy = filterSearchBy;
        this.orderHistoryPayload.filterSearchBy = filterSearchBy;
        this.filterSearch = null;
      }
    });

    this.filterSearchSubscription$ = this.orderHistoryService.filterSearch$.subscribe((filterSearch) => {
      if (this.filterSearch !== filterSearch) {
        this.filterSearch = filterSearch;
        this.orderHistoryPayload.filterSearchData = filterSearch;
        this.getOrderHistory();
        this.cdr.detectChanges();
      }
    });

    this.setOrderStatusSubscription$ = this.orderHistoryService.setOrderStatus$.subscribe((status) => {
      if (!this.orderHistoryPayload.orderStatus.includes(status)) {
        this.orderHistoryPayload.orderStatus.push(status);
      }
      this.getOrderHistory();
      this.cdr.detectChanges();
    });

    this.setOrderTypeSubscription$ = this.orderHistoryService.setOrderType$.subscribe((orderTypes) => {
      this.orderHistoryPayload.orderTypes = orderTypes;
      this.getOrderHistory();
      this.cdr.detectChanges();
    });

    this.removeOrderStatusSubscription$ = this.orderHistoryService.removeOrderStatus$.subscribe((status) => {
      this.orderHistoryPayload.orderStatus = this.orderHistoryPayload.orderStatus.filter(
        (item: any) => item !== status
      );
      this.getOrderHistory();
      this.cdr.detectChanges();
    });

    this.fromDateSubscription$ = this.orderHistoryService.fromDate$.subscribe((date) => {
      this.orderHistoryPayload.fromDate = date;
      this.getOrderHistory();
      this.cdr.detectChanges();
    });

    this.toDateSubscription$ = this.orderHistoryService.toDate$.subscribe((date) => {
      this.orderHistoryPayload.toDate = date;
      this.getOrderHistory();
      this.cdr.detectChanges();
    });

    this.selectedFilterSubscription$ = this.orderHistoryService.selectedFilter$.subscribe((status) => {
      this.orderHistoryPayload.currentPage = 0;
      this.selectedFilter = status;
      this.cdr.detectChanges();
    });

    this.clearFilterSubscription$ = this.orderHistoryService.clearFilters$.subscribe((clear) => {
      this.resultsSpinner = true;
      this.orderHistoryPayload.filterSearchData = '';
      this.orderHistoryPayload.filterSearchBy = 'orderNumber';
      this.orderHistoryPayload.fromDate = '';
      this.orderHistoryPayload.orderStatus = [];
      this.orderHistoryPayload.orderTypes = [];
      this.orderHistoryPayload.toDate = '';
      this.setDefaultOrderTypes();
      this.setDefaultSortBy();
      this.getOrderHistory();
      this.cdr.detectChanges();
    });
    this.businessType = this.currentBusinesstype.getBusinessType();

    this.totalOrdersSubscription$ = this.orderHistoryService.totalOrders$.subscribe((total) => {
      this.totalNumberOfResults = total;
      this.cdr.detectChanges();
    });

    this.breakpointSubscription$ = this.breakpointService.breakpoint$.subscribe((breakpoint) => {
      const dropdownBackdropElement = document.getElementsByClassName('cdk-overlay-backdrop')[0] as HTMLElement;
      this.isDesktop = breakpoint === BREAKPOINT.xl;

      if (dropdownBackdropElement) {
        dropdownBackdropElement.click();
      }
      if (this.isDesktop) {
        this.sideNavOpened = false;
        this.orderHistoryService.setTotalOrders(this.totalNumberOfResults);
      }
    });

    this.reloadLanguageForOrderTypeSubscription$ = this.activeLanguage$.subscribe((language) => {
      if (this.currentLanguage !== language) {
        this.resultsSpinner = true;
        this.currentLanguage = language;
        this.getOrderHistory();
      }
      // Apply custom styling for pt language
      const dropdownMenuEl = this.elementRef.nativeElement.getElementsByClassName('dropdown-menu')[0] as HTMLElement;
      if (language === 'pt') {
        this.renderer.addClass(dropdownMenuEl, 'pt-styling');
      } else {
        this.renderer.removeClass(dropdownMenuEl, 'pt-styling');
      }
      this.cdr.detectChanges();
    });
  }

  toggleSidenav(): void {
    // Hide the header, breadcrumbs and footer
    this.header.classList.add('d-none');
    this.bottomHeaderSlot.classList.add('d-none');
    this.footer.classList.add('d-none');
    this.hasOpenSideNav = true;

    // open the sidenav
    this.sidenav.toggle();
  }
  onClose(status: number): void {
    // Show the header, breadcrumbs and footer
    this.hasOpenSideNav = false;
    this.selectedFilter = status;
    this.orderHistoryService.setSelectedFilter(status);
    this.header.classList.remove('d-none');
    this.bottomHeaderSlot.classList.remove('d-none');
    this.footer.classList.remove('d-none');

    setTimeout(() => {
      // tslint:disable-next-line: no-string-literal
      this.orderHistoryContentHeight = this.matContainer._content['elementRef'].nativeElement.offsetHeight;
      // this.styleMode = {
      //   'max-height': this.orderHistoryContentHeight + 'px',
      // };
      // tslint:disable-next-line: no-string-literal
      Object.assign(this.bodyContent['style'], this.styleMode);
    }, 1000);
  }

  getOrderHistory(onScroll = false, onInit = false) {
    const orderView = this.orderView ?? OrderView.ORDERS;
    // to trigger getOrderHistory
    this.orderHistoryService.getOrderHistory$.next([orderView, onScroll, onInit]);
  }

  setOrderBackordersPage(items: any): void {
    this.hasData = Object.keys(items).length;
    if (this.hasData !== 0) {
      this.updatePaginationModel(items);
      this.orderHistoryService.setTotalOrders(items.pagination.totalNumberOfResults);
      items.results.forEach((result: any) => {
        const isResultUnique =
          this.orderHistoryResults.findIndex((resultList) => {
            if (this.orderView === OrderView.BACKORDER) {
              resultList.erpOrderNumber === result.erpOrderNumber && resultList.product.code === result.product.code;
            } else {
              resultList.erpOrderNumber === result.erpOrderNumber;
            }
          }) === -1;
        if (isResultUnique) this.orderHistoryResults.push(result);
      });
      this.showNoResult = false;
      this.cdr.detectChanges();
    } else {
      this.orderHistoryService.setTotalOrders(this.hasData);
      this.cdr.detectChanges();
    }

    if (this.features.showMaxResultsText && this.orderHistoryResults.length !== this.PaginationModel.totalResults) {
      this.isGetOrdersBackOrdersDone = false;
    } else {
      this.isGetOrdersBackOrdersDone = true;
    }

    this.resultsSpinner = false;
    this.cdr.detectChanges();
  }

  setError(): void {
    this.orderHistoryService.setTotalOrders(0);
    this.showNoResult = true;
    this.resultsSpinner = false;
    this.isGetOrdersBackOrdersDone = true;
    this.cdr.detectChanges();
  }

  onScroll() {
    let nextPage: number = this.orderHistoryPayload.currentPage + 1;
    this.isGetOrdersBackOrdersDone = false;
    if (nextPage < this.PaginationModel.totalPages) {
      this.orderHistoryPayload.currentPage = nextPage;
      this.getOrderHistory(true);
    } else {
      this.isGetOrdersBackOrdersDone = true;
    }
  }

  protected updatePaginationModel = (result: any) => {
    this.PaginationModel = {
      currentPage: result.pagination.currentPage,
      pageSize: result.pagination.pageSize,
      sort: null,
      totalPages: result.pagination.numberOfPages,
      totalResults: result.pagination.totalNumberOfResults,
    };
  };

  pageChange(page: any) {
    this.orderHistoryPayload.currentPage = page;
    this.getOrderHistory();
  }

  sortChange(sort: string) {
    if (this.orderView === OrderView.ORDERS || this.orderView === OrderView.BACKORDER) {
      this.sort = sort;
      [this.orderHistoryPayload.sort, this.orderHistoryPayload.dir] = SORT_MAPPING[sort].split(':');
      this.getOrderHistory();
    } else {
      this.orderHistoryService.sortChange$.next(sort);
    }
  }

  updateSortSelectedOption(sort: string): void {
    if (this.orderView === OrderView.INVOICE) {
      this.invoiceSort = sort;
    }
  }

  getInitSrpToggleValue() {
    this.showSrp = this.storageService.getLocalStorageItem('showSrp') === 'true';
  }

  getImageRendition(image: any) {
    let size: string;
    size = ShimanoUtilMethod.imageRendition(80);
    return ShimanoUtilMethod.newImageFormat(image, size);
  }

  checkVariant(variants: any) {
    return variants
      .filter((variant: any) => {
        if (variant.code !== this.skipVariantCategory) {
          return variant;
        }
      })
      .slice(0, 3);
  }

  getStatusIcon(statusCode: string): string {
    return getOrderHistoryStatusIcon(statusCode);
  }

  getStatusTooltip(statusCode: string): Observable<string> {
    let tooltip$: Observable<string>;
    switch (statusCode) {
      case OrderHistoryStatus.OPEN:
        tooltip$ = this.translationService.translate('buttonTooltip.orderHistory.openStatus');
        break;
      case OrderHistoryStatus.CLOSED:
        tooltip$ = this.translationService.translate('buttonTooltip.orderHistory.closeStatus');
        break;
      case OrderHistoryStatus.BACKORDER:
        tooltip$ = this.translationService.translate('buttonTooltip.orderHistory.backorderStatus');
        break;
    }
    return tooltip$;
  }

  filterBackorderSortOptions(bOptions: any[], showExpectedShipmentDate: boolean): any[] {
    if (Object.values(this.features).length) {
      const sortOptionsToBeRemoved = this.getBackorderSortOptionsToBeRemoved(showExpectedShipmentDate);
      sortOptionsToBeRemoved.forEach((optionToBeRemoved) => {
        const removeIndex = bOptions.findIndex((option) => option.code === optionToBeRemoved);
        if (removeIndex > -1) {
          bOptions.splice(removeIndex, 1);
        }
      });
    }
    return bOptions;
  }

  filterSortOptions(sortOptions: any[]): any[] {
    if (Object.values(this.features).length) {
      const sortOptionsToBeRemoved = this.getSortOptionsToBeRemoved();
      sortOptionsToBeRemoved.forEach((optionToBeRemoved) => {
        const removeIndex = sortOptions.findIndex((option) => option.code === optionToBeRemoved);
        if (removeIndex > -1) {
          sortOptions.splice(removeIndex, 1);
        }
      });
    }
    return sortOptions;
  }

  filterInvoiceOptions(sortOptions: any[]): any[] {
    if (Object.values(this.features).length) {
      const sortOptionsToBeRemoved = this.getInvoiceOptionsToBeRemoved();
      sortOptionsToBeRemoved.forEach((optionToBeRemoved) => {
        const removeIndex = sortOptions.findIndex((option) => option.code === optionToBeRemoved);
        if (removeIndex > -1) {
          sortOptions.splice(removeIndex, 1);
        }
      });
    }
    return sortOptions;
  }

  getSortOptionsToBeRemoved(): string[] {
    let toRemove: string[] = [];
    // Add more options to be removed here
    if (!this.features?.showHoldStatus) toRemove.push('holdcodeasc', 'holdcodedesc');
    if (!this.features?.showOrderRequestDate) toRemove.push('daterequestasc', 'daterequestdesc');
    if (this.features?.disabledOrderTypeSorting) toRemove.push('ordertype');
    return toRemove;
  }

  getBackorderSortOptionsToBeRemoved(showExpectedShipmentDate: boolean): string[] {
    let toRemove: string[] = [];
    // Add more options to be removed here
    if (showExpectedShipmentDate === false) toRemove.push('deliverydateasc', 'deliverydatedesc');
    if (!this.features.showBackorderRequestDate) toRemove.push('daterequestasc', 'daterequestdesc');
    if (this.features?.disabledOrderTypeSorting) toRemove.push('ordertype');
    return toRemove;
  }

  getInvoiceOptionsToBeRemoved(): string[] {
    let toRemove: string[] = [];
    // Add more options to be removed here
    if (this.features?.disabledOrderTypeSorting) toRemove.push('ordertype');
    return toRemove;
  }

  setDefaultFilter(isFirstEmit: boolean): void {
    const features = this.features;

    // Apply upon landing on Order History page only
    if (features.defaultFilter && isFirstEmit) {
      if (features.defaultFilter.fromDateInMonths) {
        const fromMonths = moment(new Date()).subtract(features.defaultFilter.fromDateInMonths, 'months');
        const date = moment.utc(fromMonths.toISOString()).local().startOf('day');
        this.orderHistoryPayload.fromDate = date.format();
        this.orderHistoryPayload.toDate = moment(new Date()).format();
      }
      this.orderHistoryPayload.orderStatus = features.defaultFilter.orderStatus || [];
    }

    // Apply upon landing on Order History page and 'order view' change
    this.setDefaultOrderTypes();
    this.setDefaultSortBy();
  }

  downloadOpenOrders() {
    this.orderHistoryService.downloadOpenOrders().subscribe(
      (success) => {},
      (error) => {
        this.messageService.add({ key: 'orders.downloadOpenOrdersError' }, GlobalMessageType.MSG_TYPE_ERROR);
      }
    );
  }

  showAddress(addresses: any): Observable<string> {
    return this.myAccountPersonalInforService.formatAddress(addresses);
  }

  ngAfterViewInit() {
    const sideNavElement = this.elementRef.nativeElement.querySelector('.mat-sidenav');
    if (this.elementRef.nativeElement.querySelector('.pt')) {
      this.renderer.addClass(sideNavElement, 'ptOrderHistorySortBy');
    }
  }

  setDefaultSortBy(): void {
    if (this.features.showBackorderRequestDate && this.orderView === OrderView.BACKORDER) {
      this.sort = 'daterequestdesc';
      this.orderHistoryPayload.sort = 'dateRequest';
      this.orderHistoryPayload.dir = 'desc';
    } else {
      this.sort = 'orderdatedesc';
      this.orderHistoryPayload.sort = 'orderDate';
      this.orderHistoryPayload.dir = 'desc';
    }
  }

  setDefaultOrderTypes(): void {
    const hasSelectedOrderTypes = this.orderHistoryPayload.orderTypes.length;
    if (hasSelectedOrderTypes) {
      return;
    }
    if (this.features.invoiceHistoryEnabled) {
      if (this.orderView === OrderView.ORDERS) {
        this.orderHistoryPayload.orderTypes = ['SO', 'PO', 'SW', 'CO'];
      } else if (this.orderView === OrderView.BACKORDER) {
        this.orderHistoryPayload.orderTypes = ['SO', 'PO', 'SW'];
      }
    }
  }

  exportCsv(): void {
    const endpoint = this.orderView === OrderView.ORDERS ? 'exportOrderHistory' : 'exportBackOrders';
    const fileName = this.orderView === OrderView.ORDERS ? 'Order History' : 'Backorder Items';
    this.orderHistoryService.exportCsv(this.orderHistoryPayload, endpoint, fileName).subscribe();
  }

  applyWideDiv(): void {
    const breadcrumbsElement = document.querySelector('app-breadcrumbs .container');
    const isBackorderViewWithAdditionalColumn = this.features.showBackorderRequestDate;
    const isInvoiceView = this.orderView === OrderView.INVOICE;

    if ((isBackorderViewWithAdditionalColumn || isInvoiceView) && this.isDesktop && this.orderHistoryElement) {
      const marginLeftValue = parseFloat(window.getComputedStyle(breadcrumbsElement).marginLeft);
      this.renderer.setStyle(this.orderHistoryElement, 'paddingLeft', `${marginLeftValue}px`);
      this.renderer.setStyle(this.orderHistoryElement, 'paddingRight', `${marginLeftValue / 4}px`);
      this.renderer.addClass(this.orderHistoryElement, 'wide-div');
    } else {
      this.removeWideDiv();
    }
  }

  removeWideDiv(): void {
    if (this.orderHistoryElement) {
      this.renderer.removeStyle(this.orderHistoryElement, 'paddingLeft');
      this.renderer.removeStyle(this.orderHistoryElement, 'paddingRight');
      this.renderer.removeClass(this.orderHistoryElement, 'wide-div');
    }
  }

  ngAfterContentChecked() {
    this.cdr.detectChanges();
  }

  @HostListener('window:resize')
  onResize(): void {
    this.applyWideDiv();
  }

  ngOnDestroy() {
    this.srpSubscription$?.unsubscribe();
    this.filterSearchSubscription$?.unsubscribe();
    this.filterSearchBySubscription$?.unsubscribe();
    this.setOrderStatusSubscription$?.unsubscribe();
    this.removeOrderStatusSubscription$?.unsubscribe();
    this.fromDateSubscription$?.unsubscribe();
    this.toDateSubscription$?.unsubscribe();
    this.clearFilterSubscription$?.unsubscribe();
    this.selectedFilterSubscription$?.unsubscribe();
    this.totalOrdersSubscription$?.unsubscribe();
    this.setOrderTypeSubscription$?.unsubscribe();
    this.getOrderHistorySubscription$?.unsubscribe();
    this.triggerOrdersSubscription$?.unsubscribe();
    this.reloadLanguageForOrderTypeSubscription$?.unsubscribe();
    this.orderHistoryService.getOrderHistory$.next([null, false, false]);
    this.removeWideDiv();
  }
}
